/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import Button from "@components/common/Button";
import { theme } from "src/styles/theme";
import { useNavigate } from "react-router-dom";
import { LoginContext } from "@context/loginContext";
import { UsuallyContext } from "@context/usuallyContex";
import { OportunityContext } from "@context/oportunityContex";
import Spinner from "@components/common/Spinner";
import Alert from "@components/common/Alert";
import { useMessageErrorToast } from "@hook/useMessageErrorToast";
import { RxUpdate } from "react-icons/rx";
import oportunityServiceInstance from "@services/Oportunity/OportunityService";
import {
  BolsaoDTO,
  BolsaoTabs,
  LimiteAtingido,
  OportunidadeBolsao,
  SuccessModal,
  VirtualizedMobileList,
  VirtualizedTable,
} from "./BolsaoHelpers";
import { cookieKeys, getCookie } from "@helpers/cookies";

const TRANSFER_LIMIT = 10;
export const PAGE_SIZE = 50;

const LS_BOLSOES = "@bolsao:bolsoes";
const LS_REFRESH_TIMESTAMP = "@bolsao:refresh_timestamp";

/**
 * Lê do localStorage o momento do último bloqueio e a duração que o backend
 * mandou naquela ocasião, devolvendo quanto ainda falta. Sem valor gravado
 * (ou em formato antigo, sem duração), não há bloqueio.
 */
const readRefreshCooldown = (): number => {
  const raw = localStorage.getItem(LS_REFRESH_TIMESTAMP);
  if (!raw) return 0;

  try {
    const parsed = JSON.parse(raw);
    const timestamp = parsed?.timestamp;
    const duration = parsed?.duration;

    if (typeof timestamp !== "number" || typeof duration !== "number") return 0;

    const elapsed = Math.floor((Date.now() - timestamp) / 1000);
    const remaining = duration - elapsed;
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
};

const saveRefreshTimestamp = (duration: number) =>
  localStorage.setItem(
    LS_REFRESH_TIMESTAMP,
    JSON.stringify({ timestamp: Date.now(), duration }),
  );
const clearRefreshTimestamp = () =>
  localStorage.removeItem(LS_REFRESH_TIMESTAMP);

const CACHE_TTL = 5 * 60 * 1000;

const readBolsoesCache = (): { data: BolsaoDTO[]; isValid: boolean } => {
  const raw = localStorage.getItem(LS_BOLSOES);
  if (!raw) return { data: [], isValid: false };

  try {
    const parsed = JSON.parse(raw);
    const isValid = Date.now() - parsed.timestamp <= CACHE_TTL;
    return {
      data: parsed.data || [],
      isValid,
    };
  } catch {
    return { data: [], isValid: false };
  }
};

const saveBolsoesCache = (data: BolsaoDTO[]) =>
  localStorage.setItem(
    LS_BOLSOES,
    JSON.stringify({ data, timestamp: Date.now() }),
  );

const clearBolsoesCache = () => localStorage.removeItem(LS_BOLSOES);

/** Mantém a tab ativa se ela ainda existir no novo retorno; senão, cai na primeira */
const resolveActiveTab = (data: BolsaoDTO[], current: string) => {
  if (current && data.some((b) => b.tipoBolsao === current)) return current;
  return data[0]?.tipoBolsao ?? "";
};

const Bolsao: React.FC = () => {
  const navigate = useNavigate();
  const { signOut, setPageSidebar } = React.useContext(LoginContext);
  const { setHashSalesSidebar, setHashDetailOportunity, setHashOportunity } =
    React.useContext(UsuallyContext);
  const { setAccessCreateOp } = React.useContext(OportunityContext);
  const idCorretor = getCookie(cookieKeys.idCorretor) || "";

  const hasFetchedRef = React.useRef(false);
  const [isUnauthorized, setIsUnauthorized] = React.useState(false);
  const [bolsoes, setBolsoes] = React.useState<BolsaoDTO[]>([]);
  const [activeTab, setActiveTab] = React.useState<string>("");
  const [loadingGetOportunity, setLoadingGetOportunity] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [allSelected, setAllSelected] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [lastTransferCount, setLastTransferCount] = React.useState(0);
  const [unidadeFilter, setUnidadeFilter] = React.useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [refreshCooldown, setRefreshCooldown] = React.useState<number>(() =>
    readRefreshCooldown(),
  );
  const [transferWarning, setTransferWarning] = React.useState(false);
  const [transferMessage, setTransferMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [hasSyncedLimits, setHasSyncedLimits] = React.useState(false);

  /** Página atual da paginação client-side */
  const [currentPage, setCurrentPage] = React.useState(1);

  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const displayMessageError = useMessageErrorToast();
  const displayMessageErrorRef = React.useRef(displayMessageError);
  React.useEffect(() => {
    displayMessageErrorRef.current = displayMessageError;
  }, [displayMessageError]);

  const isRefreshDisabled = refreshCooldown > 0 || isRefreshing;

  // ── Bolsão atual (tab ativa) ──
  const currentBolsao = React.useMemo(
    () => bolsoes.find((b) => b.tipoBolsao === activeTab),
    [bolsoes, activeTab],
  );

  const restantesPorBolsao = React.useMemo(() => {
    const map: Record<string, number> = {};
    bolsoes.forEach((b) => {
      map[b.tipoBolsao] = Math.max(0, b.limiteTotal - b.quantidadeTransferida);
    });
    return map;
  }, [bolsoes]);

  const transferenciasRestantes = restantesPorBolsao[activeTab] ?? 0;
  const transferLimit = currentBolsao?.limiteTotal ?? TRANSFER_LIMIT;
  const limiteAtingido = !!currentBolsao && transferenciasRestantes <= 0;

  const activeOportunidades = currentBolsao?.oportunidades ?? [];

  const selectedCount = selectedIds.size;
  const hasSelected = selectedCount > 0;

  const unidades = React.useMemo(
    () => Array.from(new Set(activeOportunidades.map((o) => o.unidadeNegocio))),
    [activeOportunidades],
  );

  // ── Dados filtrados (todos, sem paginar ainda) ──
  const filteredOportunidades = React.useMemo(() => {
    if (unidadeFilter.length === 0) return activeOportunidades;
    return activeOportunidades.filter((o) =>
      unidadeFilter.includes(o.unidadeNegocio),
    );
  }, [activeOportunidades, unidadeFilter]);

  // ── Dados da página atual ──
  const totalPages = Math.max(
    1,
    Math.ceil(filteredOportunidades.length / PAGE_SIZE),
  );

  const pagedOportunidades = React.useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredOportunidades.slice(start, start + PAGE_SIZE);
  }, [filteredOportunidades, currentPage]);

  // Reset página ao mudar filtro
  React.useEffect(() => {
    setCurrentPage(1);
  }, [unidadeFilter]);

  // Countdown do cooldown de refresh
  React.useEffect(() => {
    if (refreshCooldown <= 0) return;
    intervalRef.current = setInterval(() => {
      setRefreshCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          clearRefreshTimestamp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refreshCooldown > 0]);

  // ── Fetch ──
  const fetchOportunidades = React.useCallback(async (cancelled?: boolean) => {
    setLoadingGetOportunity(true);
    try {
      const response = await oportunityServiceInstance.getOportunityBolsao();

      if (cancelled) return;

      setHasSyncedLimits(true);

      if (!response && response.status >= 400 && response?.status !== 401) {
        setLoadingGetOportunity(false);
        displayMessageErrorRef.current({
          message:
            response.data ||
            "Não foi possível carregar os dados da Oportunidade",
        });
        return;
      }
      if (response.status === 401) {
        setLoadingGetOportunity(false);
        setIsUnauthorized(true);
        return;
      }
      if (
        response.status >= 400 &&
        response?.data?.toLowerCase()?.includes("aguarde 1 minuto")
      ) {
        setLoadingGetOportunity(false);
        displayMessageErrorRef.current({
          message:
            response.data || "Erro ao carregar os dados da Oportunidade.",
        });

        const { data: cachedData, isValid } = readBolsoesCache();
        if (isValid && cachedData.length > 0) {
          setBolsoes(cachedData);
          setActiveTab((prev) => resolveActiveTab(cachedData, prev));
        } else {
          clearBolsoesCache();
          setBolsoes([]);
          setActiveTab("");
        }
        return;
      }
      if (response.status >= 400) {
        setLoadingGetOportunity(false);
        displayMessageErrorRef.current({
          message:
            response.data || "Erro ao carregar os dados da Oportunidade.",
        });
        // O backend rejeitou a listagem (ex: bolsão desabilitado no
        // Salesforce). Não dá pra continuar exibindo o snapshot antigo:
        // zera o estado e invalida o cache pra não reaparecer depois.
        clearBolsoesCache();
        setBolsoes([]);
        setActiveTab("");
        setSelectedIds(new Set());
        setAllSelected(false);
        return;
      }

      // Cooldown vindo do backend: o timer começa a partir desse número
      const tempoBloqueio = response?.tempoBloqueioSegundos;
      if (typeof tempoBloqueio === "number" && tempoBloqueio > 0) {
        saveRefreshTimestamp(tempoBloqueio);
        setRefreshCooldown(tempoBloqueio);
      } else {
        clearRefreshTimestamp();
        setRefreshCooldown(0);
      }

      const data: BolsaoDTO[] = response?.bolsoes ?? [];
      setBolsoes(data);
      setActiveTab((prev) => resolveActiveTab(data, prev));
      saveBolsoesCache(data);
      setLoadingGetOportunity(false);
    } catch (error) {
      if (cancelled) return;
      setLoadingGetOportunity(false);
      console.log("ErrorFetchOportunidades", error);
    }
  }, []);

  const handleRefresh = React.useCallback(() => {
    if (isRefreshDisabled) return;
    setIsRefreshing(true);
    // O bloqueio é aplicado só quando a resposta chegar, com o
    // `tempoBloqueioSegundos` do backend. Até lá, `isRefreshing` já
    // impede um segundo clique.
    fetchOportunidades().finally(() => {
      setIsRefreshing(false);
    });
  }, [isRefreshDisabled, fetchOportunidades]);

  // Primeira carga
  React.useEffect(() => {
    let cancelled = false;

    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;

      if (isRefreshDisabled) {
        const { data: cachedData, isValid } = readBolsoesCache();

        if (isValid && cachedData.length > 0) {
          setBolsoes(cachedData);
          setActiveTab((prev) => resolveActiveTab(cachedData, prev));
          setHasSyncedLimits(true);
          return;
        }
      }

      fetchOportunidades(cancelled);
      return;
    }

    // FIX: removido o bloco que, a cada mudança de `isRefreshDisabled`
    // (ex: início/fim do cooldown de refresh), reidratava `bolsoes` a partir
    // do cache antigo do localStorage por cima do estado atual. Isso fazia
    // com que, ao clicar em "Atualizar", o dado velho (com bolsões já
    // desabilitados no Salesforce) fosse reinjetado na tela antes da nova
    // resposta da API chegar — e, se essa nova resposta viesse com erro
    // (400), o estado ficava preso nesse dado velho até um fetch bem
    // sucedido (o que só acontecia de fato após logout/login).

    return () => {
      cancelled = true;
    };
  }, [isRefreshDisabled]);

  // ── Tabs ──
  const handleTabChange = React.useCallback(
    (tipoBolsao: string) => {
      if (tipoBolsao === activeTab) return;
      setActiveTab(tipoBolsao);
      setSelectedIds(new Set());
      setAllSelected(false);
      setUnidadeFilter([]);
      setCurrentPage(1);
    },
    [activeTab],
  );

  const handleLockedTabAttempt = React.useCallback(() => {
    displayMessageError({
      message:
        "Finalize ou desmarque a seleção de oportunidades atual para trocar de bolsão.",
    });
  }, [displayMessageError]);

  // ── Seleção ──
  const handleSelectAll = React.useCallback(
    (checked: boolean) => {
      if (!checked) {
        setAllSelected(false);
        setSelectedIds(new Set());
        return;
      }
      const allIds = filteredOportunidades.map((o) => o.oportunidadeId);
      if (allIds.length > transferenciasRestantes) {
        setSelectedIds(new Set(allIds.slice(0, transferenciasRestantes)));
        setAllSelected(false);
        displayMessageError({
          message: `Você pode selecionar no máximo ${transferenciasRestantes} Oportunidade(s). Apenas as primeiras foram selecionadas.`,
        });
      } else {
        setSelectedIds(new Set(allIds));
        setAllSelected(true);
      }
    },
    [filteredOportunidades, transferenciasRestantes, displayMessageError],
  );

  const handleSelectOne = React.useCallback(
    (id: string, checked: boolean) => {
      if (checked && selectedIds.size >= transferenciasRestantes) {
        displayMessageError({
          message: `Limite atingido. Você só pode selecionar ${transferenciasRestantes} Oportunidade(s) por dia.`,
        });
        return;
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        checked ? next.add(id) : next.delete(id);
        return next;
      });
    },
    // selectedIds.size como dep — Set é mutável, usar .size garante valor atualizado
    [selectedIds.size, transferenciasRestantes, displayMessageError],
  );

  React.useEffect(() => {
    setAllSelected(
      filteredOportunidades.length > 0 &&
        selectedIds.size === filteredOportunidades.length,
    );
  }, [selectedIds.size, filteredOportunidades]);

  // ── Transferência ──
  const handleTransfer = React.useCallback(async () => {
    setLoading(true);
    try {
      if (selectedIds.size === 0) return;

      const selectedOportunidades = activeOportunidades.filter((op) =>
        selectedIds.has(op.oportunidadeId),
      );
      const opportunityIdSalesforce = selectedOportunidades.map(
        (op) => op.opportunityIdSalesforce,
      );

      const response = await oportunityServiceInstance.transferOportunityBolsao(
        {
          opportunityIdSalesforce,
          contactIdSalesforce: idCorretor,
          tipoBolsao: activeTab,
        },
      );

      if (!response && response.status >= 400 && response?.status !== 401) {
        setLoading(false);
        displayMessageErrorRef.current({
          message:
            response?.data?.erro ||
            response?.data?.mensagem ||
            response?.data?.message ||
            (typeof response?.data === "string" ? response.data : null) ||
            "Não foi possível carregar os dados da Oportunidade",
        });
        return;
      }
      if (response.status >= 400 && response?.status !== 401) {
        setLoading(false);
        displayMessageErrorRef.current({
          message:
            response?.data?.erro ||
            response?.data?.mensagem ||
            response?.data?.message ||
            (typeof response?.data === "string" ? response.data : null) ||
            "Erro ao carregar os dados da Oportunidade.",
        });
        return;
      }

      if (response.status === 401) {
        setLoading(false);
        setIsUnauthorized(true);
        return;
      }

      const { flagOpJaTransferida, qtdeSucesso, mensagem } = response;

      const count = qtdeSucesso ?? selectedIds.size;
      setLastTransferCount(count);
      setTransferWarning(flagOpJaTransferida ?? false);
      setTransferMessage(mensagem ?? "");

      const removedIds = selectedIds;
      const tabDaTransferencia = activeTab;

      const updatedBolsoes = bolsoes.map((b) =>
        b.tipoBolsao === tabDaTransferencia
          ? {
              ...b,
              quantidadeTransferida: b.quantidadeTransferida + count,
              oportunidades: b.oportunidades.filter(
                (op) => !removedIds.has(op.oportunidadeId),
              ),
            }
          : b,
      );

      setBolsoes(updatedBolsoes);
      saveBolsoesCache(updatedBolsoes);

      setSelectedIds(new Set());
      setAllSelected(false);
      setShowSuccessModal(true);
      setLoading(false);
    } catch (error) {
      console.log("Erro ao transferir", error);
      setLoading(false);
      displayMessageError({
        message: "Erro inesperado ao transferir oportunidades.",
      });
    }
  }, [
    selectedIds,
    activeOportunidades,
    activeTab,
    idCorretor,
    bolsoes,
    displayMessageError,
  ]);

  const toggleUnidade = (unidade: string) =>
    setUnidadeFilter((prev) =>
      prev.includes(unidade)
        ? prev.filter((u) => u !== unidade)
        : [...prev, unidade],
    );

  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll suave ao topo da tabela ao trocar de página
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const navigateToMinhasOportunidades = () => {
    localStorage.setItem("@option", "Minhas oportunidades");
    setPageSidebar("Minhas oportunidades");
    setHashOportunity("MyOportunityComponent");
    setHashSalesSidebar("MyOportunityComponent");
    setHashDetailOportunity("DetailOportunity");
    navigate("/oportunidades/minhas-oportunidades");
    setAccessCreateOp({
      nomeDaConta: "",
      oportunidadeId: "",
      oportunityIdSalesforce: "",
    });
  };

  return (
    <Box
      display="flex"
      flexDir="column"
      p={{ base: 3, sm: 4, md: 4 }}
      left={{ base: 0, lg: "230px" }}
      mt={{ base: "20px", sm: "20px", md: "20px", lg: "0px" }}
      overflow="hidden"
      bg={theme.disable100}
      h="92vh"
    >
      <Alert
        onOpen={isUnauthorized}
        title="Sessão expirada"
        titleButton="Fazer login novamente"
        description="Sua sessão expirou, faça login novamente para ter acesso."
        onClick={() => signOut()}
      />

      {/* ── Header ── */}
      <Box w="100%" mb={{ base: 3, md: 3 }}>
        <Flex w="100%" align="flex-start" justify="space-between">
          <Text
            fontSize={{ base: "20px", md: "24px", lg: "26px" }}
            fontWeight="600"
            color={theme.text}
            lineHeight="1.3"
            whiteSpace="nowrap"
          >
            {limiteAtingido
              ? "Oportunidades do bolsão"
              : "Bolsão de Oportunidades"}
          </Text>

          <Box flexShrink={0} w="fit-content">
            {hasSelected ? (
              <Button
                title={
                  <>
                    {loading ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="100px"
                      >
                        <Spinner />
                      </Box>
                    ) : (
                      <Flex align="center" gap="6px">
                        <RxUpdate />
                        Transferir ({selectedCount})
                      </Flex>
                    )}
                  </>
                }
                onClick={handleTransfer}
                disabled={loading}
              />
            ) : (
              <Button
                title={
                  <Flex>
                    {isRefreshing ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        w="100px"
                      >
                        <Spinner />
                      </Box>
                    ) : (
                      <Text
                        display="flex"
                        w="fit-content"
                        gap="6px"
                        align="center"
                      >
                        <RxUpdate />
                        {refreshCooldown > 0
                          ? `Atualizar (${refreshCooldown}s)`
                          : "Atualizar"}
                      </Text>
                    )}
                  </Flex>
                }
                colorInverter
                onClick={handleRefresh}
                disabled={isRefreshDisabled}
              />
            )}
          </Box>
        </Flex>

        {hasSyncedLimits && currentBolsao && (
          <Text
            fontSize={{ base: "12px", md: "13px" }}
            color={theme.text50}
            mt="1px"
          >
            Você pode ver abaixo quantas oportunidades você ainda pode
            transferir de cada bolsão
          </Text>
        )}
      </Box>

      {!loadingGetOportunity && (
        <BolsaoTabs
          bolsoes={bolsoes.map((b) => ({
            tipoBolsao: b.tipoBolsao,
            count: restantesPorBolsao[b.tipoBolsao] ?? 0,
          }))}
          activeTab={activeTab}
          locked={hasSelected}
          onChange={handleTabChange}
          onLockedAttempt={handleLockedTabAttempt}
        />
      )}

      <Box flex="1" minH={0}>
        {loadingGetOportunity ? (
          <Flex justify="center" align="center" h="71vh">
            <Spinner />
          </Flex>
        ) : (
          <>
            {limiteAtingido ? (
              <LimiteAtingido onNavigate={navigateToMinhasOportunidades} />
            ) : (
              <>
                {/* ── MOBILE ── */}
                <Box
                  display={{ base: "flex", md: "none" }}
                  flexDir="column"
                  gap={3}
                >
                  <VirtualizedMobileList
                    oportunidades={pagedOportunidades}
                    selectedIds={selectedIds}
                    selectedCount={selectedCount}
                    allSelected={allSelected}
                    onSelectOne={handleSelectOne}
                    onSelectAll={handleSelectAll}
                    unidades={unidades}
                    unidadeFilter={unidadeFilter}
                    toggleUnidade={toggleUnidade}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredOportunidades.length}
                    onPageChange={handlePageChange}
                    setIsFilterOpen={setIsFilterOpen}
                    isFilterOpen={isFilterOpen}
                    pageSize={PAGE_SIZE}
                  />
                </Box>

                {/* ── DESKTOP ── */}
                <Box display={{ base: "none", md: "block" }}>
                  <VirtualizedTable
                    oportunidades={pagedOportunidades}
                    selectedIds={selectedIds}
                    selectedCount={selectedCount}
                    allSelected={allSelected}
                    onSelectOne={handleSelectOne}
                    onSelectAll={handleSelectAll}
                    unidades={unidades}
                    unidadeFilter={unidadeFilter}
                    toggleUnidade={toggleUnidade}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredOportunidades.length}
                    onPageChange={handlePageChange}
                    pageSize={PAGE_SIZE}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>
      <SuccessModal
        isOpen={showSuccessModal}
        count={lastTransferCount}
        hasWarning={transferWarning}
        message={transferMessage}
        onClose={() => setShowSuccessModal(false)}
        onNavigate={() => {
          setShowSuccessModal(false);
          navigateToMinhasOportunidades();
        }}
      />
    </Box>
  );
};

export default React.memo(Bolsao);
